/**
 * train-log.ts — 训练会话记录(单一真源)
 *
 * localStorage `gto-train-log-v1`: { days, sessions }
 * 打卡日 + 最近 50 条会话摘要;供 UI 画点阵/sparkline。
 * 读写模式照抄 quiz-progress 的 snapshot/subscribe/write 三件套。
 */

export const TRAIN_LOG_KEY = "gto-train-log-v1";
const LOG_EVENT = "gto-train-log";
const MAX_SESSIONS = 50;

export type SessionSummary = {
  mode: string;
  total: number;
  correct: number;
  durationMs: number;
  avgDecisionMs: number;
  date: string; // YYYY-MM-DD
};

export type TrainLog = {
  days: string[]; // YYYY-MM-DD 打卡日,去重
  sessions: SessionSummary[];
};

export const EMPTY_TRAIN_LOG: TrainLog = { days: [], sessions: [] };

let logCache: TrainLog = EMPTY_TRAIN_LOG;
let logRaw: string | null = null;

function todayIso(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalize(raw: unknown): TrainLog {
  if (!raw || typeof raw !== "object") return { ...EMPTY_TRAIN_LOG };
  const o = raw as Partial<TrainLog>;
  const days = Array.isArray(o.days)
    ? [...new Set(o.days.filter((d): d is string => typeof d === "string"))]
    : [];
  const sessions = Array.isArray(o.sessions)
    ? o.sessions.filter(
        (s): s is SessionSummary =>
          !!s &&
          typeof s === "object" &&
          typeof s.mode === "string" &&
          typeof s.total === "number" &&
          typeof s.correct === "number" &&
          typeof s.durationMs === "number" &&
          typeof s.avgDecisionMs === "number" &&
          typeof s.date === "string",
      )
    : [];
  return { days, sessions };
}

export function getTrainLogSnapshot(): TrainLog {
  let raw: string;
  try {
    raw = localStorage.getItem(TRAIN_LOG_KEY) || "{}";
  } catch {
    return logCache;
  }
  if (raw !== logRaw) {
    logRaw = raw;
    try {
      logCache = normalize(JSON.parse(raw));
    } catch {
      logCache = { ...EMPTY_TRAIN_LOG };
    }
  }
  return logCache;
}

export function subscribeTrainLog(onChange: () => void): () => void {
  window.addEventListener("storage", onChange);
  window.addEventListener(LOG_EVENT, onChange);
  return () => {
    window.removeEventListener("storage", onChange);
    window.removeEventListener(LOG_EVENT, onChange);
  };
}

function writeLog(next: TrainLog) {
  try {
    localStorage.setItem(TRAIN_LOG_KEY, JSON.stringify(next));
    logRaw = null; // force snapshot refresh
    window.dispatchEvent(new Event(LOG_EVENT));
  } catch {
    // localStorage 不可用时静默丢弃
  }
}

export function loadTrainLog(): TrainLog {
  return getTrainLogSnapshot();
}

/** 标记打卡日(默认今天);days 去重 */
export function markDay(date?: string): void {
  const d = date ?? todayIso();
  const cur = getTrainLogSnapshot();
  if (cur.days.includes(d)) return;
  writeLog({ ...cur, days: [...cur.days, d] });
}

/** 追加一条会话摘要;超出 MAX_SESSIONS 裁最旧 */
export function logSession(summary: SessionSummary): void {
  const cur = getTrainLogSnapshot();
  const sessions = [...cur.sessions, summary];
  while (sessions.length > MAX_SESSIONS) sessions.shift();
  writeLog({ ...cur, sessions });
}
